import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Volume2, Pause, Play, Square } from "lucide-react";
import { useListen } from "./listen/useListen";

/* Inline "Listen to this page" pill lives inside the Navbar utility row.
   Replaces the older floating GlobalListen pill so it never overlaps content
   (Get-App banner, chat bubble, etc). Scans <main> for meaningful text; hides
   itself on admin / auth / thin app-shell routes and when the page has its
   own in-content ListenButton. */
const HIDE_ON = [
  "/admin", "/auth", "/app-login", "/auth-complete", "/delete-account",
  "/embed", "/widgets", "/travel-day", "/quote/", "/dashboard",
  "/expenses", "/trip-expenses/shared", "/my-social-pulse", "/locker",
  "/family-expense-hub", "/documents-locker", "/moderation", "/contact",
];
const EXCLUDE_SELECTORS = "nav, header, footer, aside, form, [role='navigation'], [data-testid='navbar'], [data-testid='footer']";

function measureText(main) {
  if (!main) return 0;
  const clone = main.cloneNode(true);
  clone.querySelectorAll(EXCLUDE_SELECTORS).forEach((n) => n.remove());
  return (clone.innerText || "").replace(/\s+/g, " ").trim().length;
}

export default function NavListenButton({ compact = false }) {
  const { pathname } = useLocation();
  const [targetId, setTargetId] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (HIDE_ON.some((p) => pathname.startsWith(p))) { setTargetId(null); return undefined; }
    const check = () => {
      if (!mounted.current) return;
      const main = document.querySelector("main");
      if (!main) { setTargetId(null); return; }
      // Skip if the page already renders its own in-content Listen controls.
      if (document.querySelector("[data-testid='listen-controls']:not([data-nav='1'])")) {
        setTargetId(null); return;
      }
      if (measureText(main) < 400) { setTargetId(null); return; }
      if (!main.id) main.id = "tp-global-listen-target";
      setTargetId(main.id);
    };
    const t1 = setTimeout(check, 400);
    const t2 = setTimeout(check, 1400);
    return () => { mounted.current = false; clearTimeout(t1); clearTimeout(t2); };
  }, [pathname]);

  if (!targetId) return null;
  return <ListenPill targetId={targetId} compact={compact} />;
}

function ListenPill({ targetId, compact }) {
  const { supported, state, toggle, stop } = useListen(targetId);
  if (!supported) return null;
  const idle = state === "idle";
  return (
    <div
      data-testid="listen-controls"
      data-nav="1"
      className="inline-flex items-center gap-0.5 border border-emerald-300 bg-white/90 rounded-full pl-0.5 pr-0.5 py-0.5"
    >
      <button
        onClick={toggle}
        data-testid="nav-listen-toggle"
        title={state === "playing" ? "Pause reading" : "Listen to this page"}
        aria-label={state === "playing" ? "Pause reading" : "Listen to this page"}
        className={`inline-flex items-center gap-1.5 ${compact ? "px-2.5" : "px-3"} py-1 rounded-full text-xs font-700 text-emerald-800 hover:bg-emerald-50 transition-colors`}
      >
        {state === "playing" ? <Pause className="w-3.5 h-3.5" strokeWidth={2.2} />
          : state === "paused" ? <Play className="w-3.5 h-3.5" strokeWidth={2.2} />
          : <Volume2 className="w-3.5 h-3.5" strokeWidth={2.2} />}
        <span className={compact ? "hidden md:inline" : ""}>
          {idle ? "Listen" : state === "playing" ? "Pause" : "Resume"}
        </span>
      </button>
      {!idle && (
        <button
          onClick={stop}
          data-testid="nav-listen-stop"
          title="Stop reading"
          aria-label="Stop reading"
          className="p-1 rounded-full text-zinc-500 hover:text-rose-600 hover:bg-rose-50"
        >
          <Square className="w-3.5 h-3.5" strokeWidth={2.2} />
        </button>
      )}
    </div>
  );
}
